import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, StatusPill, SandboxNotice, fmtMoney, fmtDate } from "@/components/Peep";

const btn = "rounded-lg border px-2 py-1 text-[0.65rem] font-semibold";

export default function AdminTransactions() {
  const [pays, setPays] = useState([]);
  const [subs, setSubs] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/payments").then((r) => setPays(r.data.payments)).catch(() => {});
    api.get("/admin/subscriptions").then((r) => setSubs(r.data.subscriptions)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const run = async (url, body, msg, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      await api.post(url, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div data-testid="admin-transactions" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Memberships & transactions</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">Sandbox tools send signed test webhooks through the same verified handler a real processor would use.</p>
      </div>
      <SandboxNotice />
      <Panel title={`Memberships (${subs.length})`}>
        <div className="flex flex-col gap-2">
          {subs.length === 0 && <p className="text-sm text-[#a7a0af]">No memberships yet.</p>}
          {subs.map((s) => (
            <div key={s.id} data-testid={`admin-sub-${s.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm">
              <div>
                <p className="font-semibold">{s.plan_name} <span className="text-xs font-normal text-[#a7a0af]">· {s.user_email}</span></p>
                <p className="text-xs text-[#a7a0af]">Period ends {fmtDate(s.current_period_end)}{s.cancel_at_period_end ? " · cancels at period end" : ""}{s.end_reason ? ` · ${s.end_reason.replace(/_/g, " ")}` : ""}</p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <StatusPill status={s.status} />
                {["active", "past_due"].includes(s.status) && (
                  <>
                    <button className={`${btn} border-emerald-400/30 text-emerald-300`} data-testid={`renew-ok-${s.id}`} onClick={() => run(`/admin/subscriptions/${s.id}/simulate-renewal`, { outcome: "success" }, "Renewal succeeded")}>Renew ✓</button>
                    <button className={`${btn} border-red-400/30 text-red-300`} data-testid={`renew-fail-${s.id}`} onClick={() => run(`/admin/subscriptions/${s.id}/simulate-renewal`, { outcome: "decline" }, "Renewal declined")}>Renew ✗</button>
                    <button className={`${btn} border-white/15 text-[#c7c1cf]`} data-testid={`end-period-${s.id}`} onClick={() => run(`/admin/subscriptions/${s.id}/end-period-now`, {}, "Period ended", "End this membership period now (sandbox)?")}>End period now</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </Panel>
      <Panel title={`Payments (${pays.length})`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="text-[0.65rem] uppercase tracking-wider text-[#a7a0af]">
              <tr><th className="py-2">Date</th><th>User</th><th>Plan</th><th>Type</th><th>Amount</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {pays.map((p) => (
                <tr key={p.id} data-testid={`admin-pay-${p.id}`} className="border-t border-white/10">
                  <td className="py-2 text-xs">{fmtDate(p.created_at)}</td>
                  <td className="text-xs">{p.user_email}</td>
                  <td className="text-xs">{p.plan_name}</td>
                  <td className="text-xs capitalize">{p.type}{p.renewal ? " (renewal)" : ""}</td>
                  <td>{fmtMoney(p.amount)}{p.refunded_amount > 0 && <span className="block text-[0.6rem] text-[#a7a0af]">refunded {fmtMoney(p.refunded_amount)}</span>}</td>
                  <td><StatusPill status={p.status} /></td>
                  <td className="whitespace-nowrap text-right">
                    {p.type === "charge" && p.status === "succeeded" && p.amount - (p.refunded_amount || 0) > 0 && (
                      <>
                        <button className={`${btn} mr-1 border-[#6a00ff]/40 text-[#c9a6ff]`} data-testid={`refund-${p.id}`} onClick={() => run(`/admin/payments/${p.id}/refund`, { revoke_access: true }, "Refund recorded", "Refund this payment in full and revoke the membership?")}>Refund</button>
                        <button className={`${btn} border-red-400/30 text-red-300`} data-testid={`chargeback-${p.id}`} onClick={() => run(`/admin/payments/${p.id}/chargeback`, {}, "Chargeback recorded", "Record a chargeback for this payment and revoke the membership?")}>Chargeback</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
